import { MessagingPlatform } from '@this+that/models';

import {
  ApplyGeneratedDraftParams,
  ApplyRevisionParams,
  DoBoxActiveReply,
  DoBoxReplyLastSent,
  EnterReplyParams,
  EnterReplyPrefill,
  HandlerSet,
  MarkSentParams,
  ReplyDraft,
} from './types';
import {
  DoBoxReplyDraftKind,
  DoBoxReplyDraftStatus,
  DoBoxReplyPanelMode,
} from '../../enums';

export const buildReplyDraftKey = (itemId: string, messageId: string): string =>
  `${itemId}:${messageId}`;

const createStubDraft = (
  itemId: string,
  messageId: string,
  kind: DoBoxReplyDraftKind,
  autoDraft = false,
  prefill?: EnterReplyPrefill,
  platform?: MessagingPlatform,
): ReplyDraft => ({
  itemId,
  messageId,
  kind,
  platform,
  slackTarget: prefill?.slackTarget,
  to: prefill?.to ?? [],
  cc: prefill?.cc ?? [],
  subject: prefill?.subject ?? '',
  body: '',
  resolvesItem: false,
  status: autoDraft
    ? DoBoxReplyDraftStatus.GENERATING
    : DoBoxReplyDraftStatus.READY,
  isUserEdited: false,
});

const updateDraft = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
  updater: (draft: ReplyDraft) => Partial<ReplyDraft>,
  fallbackKind: DoBoxReplyDraftKind = DoBoxReplyDraftKind.REPLY,
) => {
  set((state) => {
    const key = buildReplyDraftKey(itemId, messageId);
    const current =
      state.draftsByKey[key] ??
      createStubDraft(itemId, messageId, fallbackKind);
    return {
      draftsByKey: {
        ...state.draftsByKey,
        [key]: { ...current, ...updater(current) },
      },
    };
  });
};

const switchDraftKind = (
  drafts: Record<string, ReplyDraft>,
  key: string,
  draft: ReplyDraft,
  kind: DoBoxReplyDraftKind,
  prefill?: EnterReplyPrefill,
): Record<string, ReplyDraft> =>
  draft.kind === kind
    ? drafts
    : {
        ...drafts,
        [key]: {
          ...draft,
          kind,
          to: prefill?.to ?? [],
          cc: prefill?.cc ?? [],
          subject: prefill?.subject ?? '',
        },
      };

export const handleEnterReply = (
  set: HandlerSet,
  {
    itemId,
    messageId,
    kind,
    autoDraft = false,
    prefill,
    platform,
  }: EnterReplyParams,
) => {
  set((state) => {
    const key = buildReplyDraftKey(itemId, messageId);
    const existingDraft = state.draftsByKey[key];
    // A SENT draft left behind for this exact (itemId, messageId) pair is
    // never reused — reusing it is the resend loop (the already-sent body
    // reappears ready to send again). Treat it as absent so a later Reply
    // click on the same message always starts a fresh draft.
    const reusableDraft =
      existingDraft && existingDraft.status !== DoBoxReplyDraftStatus.SENT
        ? existingDraft
        : undefined;
    const activeReply: DoBoxActiveReply = { itemId, messageId };
    return {
      modeByItemId: {
        ...state.modeByItemId,
        [itemId]: DoBoxReplyPanelMode.REPLY,
      },
      activeReply,
      draftsByKey: reusableDraft
        ? switchDraftKind(state.draftsByKey, key, reusableDraft, kind, prefill)
        : {
            ...state.draftsByKey,
            [key]: createStubDraft(
              itemId,
              messageId,
              kind,
              autoDraft,
              prefill,
              platform,
            ),
          },
    };
  });
};

export const handleBackToOptions = (set: HandlerSet, itemId: string) => {
  set((state) => ({
    modeByItemId: {
      ...state.modeByItemId,
      [itemId]: DoBoxReplyPanelMode.OPTIONS,
    },
    activeReply:
      state.activeReply?.itemId === itemId ? null : state.activeReply,
  }));
};

export const handleEnterChat = (set: HandlerSet, itemId: string) => {
  set((state) => ({
    modeByItemId: {
      ...state.modeByItemId,
      [itemId]: DoBoxReplyPanelMode.CHAT,
    },
    activeReply:
      state.activeReply?.itemId === itemId ? null : state.activeReply,
  }));
};

export const handleSetDraftGenerating = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
) => {
  updateDraft(set, itemId, messageId, () => ({
    status: DoBoxReplyDraftStatus.GENERATING,
  }));
};

export const handleApplyGeneratedDraft = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
  draft: ApplyGeneratedDraftParams,
) => {
  updateDraft(set, itemId, messageId, () => ({
    ...draft,
    status: DoBoxReplyDraftStatus.READY,
    isUserEdited: false,
  }));
};

export const handleApplyRevision = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
  revision: ApplyRevisionParams,
) => {
  updateDraft(set, itemId, messageId, () => ({
    body: revision.body,
    status: DoBoxReplyDraftStatus.READY,
  }));
};

export const handleEditBody = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
  body: string,
) => {
  updateDraft(set, itemId, messageId, () => ({
    body,
    isUserEdited: true,
  }));
};

export const handleSetRecipients = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
  recipients: { to: ReplyDraft['to']; cc: ReplyDraft['cc'] },
) => {
  updateDraft(set, itemId, messageId, () => ({
    to: recipients.to,
    cc: recipients.cc,
    isUserEdited: true,
  }));
};

export const handleMarkSending = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
) => {
  updateDraft(set, itemId, messageId, () => ({
    status: DoBoxReplyDraftStatus.SENDING,
  }));
};

export const handleMarkSent = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
  receipt: MarkSentParams,
) => {
  set((state) => {
    const key = buildReplyDraftKey(itemId, messageId);
    // Cleared, not kept as status: SENT — the SENT view reads `lastSent`,
    // never `draftsByKey`, and leaving the entry behind is what let a later
    // Reply click on the same message resurface the already-sent body.
    const { [key]: _sentDraft, ...remainingDrafts } = state.draftsByKey;
    const lastSent: DoBoxReplyLastSent = {
      itemId,
      messageId,
      firstLine: receipt.firstLine,
      sentAt: receipt.sentAt,
      toName: receipt.toName,
      markedDone: receipt.markedDone,
      ...(receipt.sentChatMessage && {
        sentChatMessage: receipt.sentChatMessage,
      }),
    };
    return {
      modeByItemId: {
        ...state.modeByItemId,
        [itemId]: DoBoxReplyPanelMode.SENT,
      },
      activeReply:
        state.activeReply?.itemId === itemId &&
        state.activeReply?.messageId === messageId
          ? null
          : state.activeReply,
      draftsByKey: remainingDrafts,
      lastSent,
    };
  });
};

export const handleMarkError = (
  set: HandlerSet,
  itemId: string,
  messageId: string,
) => {
  updateDraft(set, itemId, messageId, () => ({
    status: DoBoxReplyDraftStatus.ERROR,
  }));
};
